import debounce from 'debounce';
import PropTypes from 'prop-types';
import cx from "ramda-extension/es/cx";
import isNilOrEmpty from "ramda-extension/es/isNilOrEmpty";
import notEqual from "ramda-extension/es/notEqual";
import React, { Children, cloneElement, Component, isValidElement } from 'react';
import { withDefaultComponentProps } from '../../../decorators/withDefaultComponentProps';
import Tooltip from '../../../molecules/Tooltip';
import when from '../../../utils/when';
import InputControlPanel from '../InputControlPanel';
const COMPONENT_NAME = 'Input';
const isNotPasswordInput = notEqual('password');
class Input extends Component {
    constructor(props){
        super(props);
        this.state = {
            showTooltipWithLongLabel: false,
            showTooltipWithLongText: false
        };
        this.largeTextTooltipValue = null;
        this.prevValue = null;
        this.inputId = crypto.randomUUID();
    }
    componentDidMount() {
        window.addEventListener('resize', this.onWindowResized);
        this.addDebouncedTitleToInputWithLongLabel();
    }
    componentDidUpdate(prevProps) {
        if (isNilOrEmpty(this.props.value) && !isNilOrEmpty(this.prevValue) || !isNilOrEmpty(this.props.value) && isNilOrEmpty(this.prevValue)) {
            setTimeout(this.addDebouncedTitleToInputWithLongLabel.bind(this), 400);
        } else if (prevProps.value !== this.props.value) {
            this.addDebouncedTitleToInputWithLongLabel();
        }
        this.prevValue = this.props.value;
    }
    componentWillUnmount() {
        window.removeEventListener('resize', this.onWindowResized);
    }
    /**
	 * Computes the scrollWidth for input element.
	 * Fixes bug in IE11, where elem.scrollWidth always incorrectly equals elem.offsetWidth.
	 * For other browsers, method returns same value as elem.scrollWidth does
	 * Inspired by https://github.com/gregwhitworth/scrollWidthPolyfill
	 *
	 * @param input - input element to compute scrollWidth for
	 * @param widthValueProp - input element prop to evaluate content width (value or placeholder)
	 */ getInputScrollWidth(input, widthValueProp) {
        const overrideStyles = [
            {
                name: 'position',
                value: 'absolute'
            },
            {
                name: 'float',
                value: 'left'
            },
            {
                name: 'visibility',
                value: 'hidden'
            },
            {
                name: 'width',
                value: 'auto'
            }
        ];
        const computedStyles = getComputedStyle(input);
        let elem = document.createElement('div');
        if (computedStyles) {
            const cssKeys = Object.keys(computedStyles);
            cssKeys.forEach((prop)=>elem.style.setProperty(prop, computedStyles[prop]));
        }
        overrideStyles.forEach((overrideStyle)=>elem.style.setProperty(overrideStyle.name, overrideStyle.value));
        elem.textContent = input[widthValueProp];
        elem = document.body.appendChild(elem);
        const inputScrollWidth = this.getElementContentWidth(elem);
        document.body.removeChild(elem);
        return Math.max(this.getElementContentWidth(input), inputScrollWidth);
    }
    getElementContentWidth(element) {
        const computedStyles = getComputedStyle(element);
        let width = element.clientWidth;
        if (computedStyles) {
            width = width - parseInt(computedStyles.paddingLeft) - parseInt(computedStyles.paddingRight);
        }
        return width;
    }
    addDebouncedTitleToInputWithLongLabel = debounce(()=>{
        const elem = this.spanElementWithLabel;
        let showTooltipWithLongLabel = false;
        let showTooltipWithLongText = false;
        // scrollWidth > offsetWidth could be replaced when problems with Internet Explorer occurs
        // const isLongLabel = elem.scrollWidth > Math.ceil(elem.getBoundingClientRect().width);
        // for more info see https://jira.cpas.cz/browse/GEF-599
        if (elem?.offsetWidth && elem.scrollWidth) {
            showTooltipWithLongLabel = elem.scrollWidth > elem.offsetWidth;
        }
        if (this.inputElement?.offsetWidth && this.inputElement.scrollWidth) {
            const widthValueProp = !this.inputElement.value && !!this.inputElement.placeholder ? 'placeholder' : 'value';
            showTooltipWithLongText = this.getInputScrollWidth(this.inputElement, widthValueProp) > this.getElementContentWidth(this.inputElement);
        }
        let activeElement = null;
        if (typeof document !== 'undefined') {
            activeElement = document.activeElement;
        }
        this.setState({
            showTooltipWithLongLabel,
            showTooltipWithLongText
        }, ()=>{
            if (showTooltipWithLongText) {
                this.handleInputRefocus(activeElement);
            }
        });
    }, 400);
    onWindowResized = ()=>{
        this.addDebouncedTitleToInputWithLongLabel();
    };
    handleInputRefocus = (activeElement)=>{
        if (this.inputElement?.isEqualNode(activeElement)) {
            this.inputElement.focus();
        }
    };
    render() {
        const { className, htmlFor, floatingLabelShrink, floatingLabelClassName, floatingLabelText, controlClassName, controls, onControlsClick, asyncValidationInProgress, onInputWrapperClick, children, hasError, disabled, readOnly, multiline, sizeSmall, tooltip, type, outline, required, displayRequiredClassName, inputWrapperProps } = this.props;
        const { showTooltipWithLongLabel, showTooltipWithLongText } = this.state;
        const labelElement = when(floatingLabelText, /*#__PURE__*/ React.createElement("label", {
            className: cx('Input-labelWrapper', {
                'Input-labelWrapper--shrink': floatingLabelShrink
            }, floatingLabelClassName),
            htmlFor: htmlFor
        }, /*#__PURE__*/ React.createElement("span", {
            ref: (el)=>{
                this.spanElementWithLabel = el;
            },
            className: cx('Input-label', {
                'Input-label--required': displayRequiredClassName && required,
                'Input-label--shrink': floatingLabelShrink
            })
        }, floatingLabelText)));
        const inputElement = /*#__PURE__*/ React.createElement("div", {
            className: cx('Input-controlWrapper', {
                'Input-controlWrapper--shrink': floatingLabelShrink
            }, controlClassName),
            onClick: onInputWrapperClick
        }, Children.map(children, (child)=>{
            const isInputElement = child && child.type.displayName === 'InputElement';
            if (/*#__PURE__*/ isValidElement(child) && (child.type === 'input' || isInputElement)) {
                this.largeTextTooltipValue = !!child.props.placeholder && !child.props.value ? child.props.placeholder : child.props.value;
                return /*#__PURE__*/ cloneElement(child, {
                    [isInputElement ? 'inputElementRef' : 'ref']: (el)=>{
                        this.inputElement = el;
                        if (!isInputElement) {
                            const { ref } = child;
                            if (typeof ref === 'function') {
                                ref(el);
                            }
                        }
                    },
                    id: child.props.id ?? this.inputId
                });
            }
            return child;
        }));
        const input = /*#__PURE__*/ React.createElement("div", {
            className: cx('Input', {
                'Input--outline': outline
            }, {
                'Input--noLabel': !labelElement
            }, {
                'Input--error': hasError
            }, {
                'Input--disabled': disabled
            }, {
                'Input--readOnly': readOnly
            }, {
                'Input--typeMultiline': multiline
            }, {
                'Input--sizeSmall': sizeSmall
            }, className),
            ...inputWrapperProps
        }, labelElement, inputElement, asyncValidationInProgress && /*#__PURE__*/ React.createElement("div", {
            className: "Input-asyncPreloader Preloader"
        }), /*#__PURE__*/ React.createElement(InputControlPanel, {
            className: controlClassName,
            onClick: onControlsClick,
            asyncValidationInProgress: asyncValidationInProgress
        }, controls));
        const shouldShowTooltipWithLongText = isNotPasswordInput(type) && showTooltipWithLongText && this.largeTextTooltipValue;
        if (tooltip || showTooltipWithLongLabel || shouldShowTooltipWithLongText) {
            return /*#__PURE__*/ React.createElement(Tooltip, {
                tip: input,
                place: "top",
                effect: "solid",
                eventOff: "focusout",
                hideIcon: true,
                contentClassName: "Textfield-tooltip"
            }, showTooltipWithLongLabel && /*#__PURE__*/ React.createElement("div", {
                className: showTooltipWithLongText ? 'Tooltip-description' : 'Tooltip-heading'
            }, floatingLabelText), shouldShowTooltipWithLongText && /*#__PURE__*/ React.createElement("div", {
                className: 'Tooltip-heading'
            }, this.largeTextTooltipValue), tooltip && /*#__PURE__*/ React.createElement("div", {
                className: 'Tooltip-description'
            }, tooltip));
        } else {
            return input;
        }
    }
}
Input.propTypes = {
    asyncValidationInProgress: PropTypes.bool,
    children: PropTypes.node,
    className: PropTypes.string,
    controlClassName: PropTypes.string,
    controls: PropTypes.node,
    disabled: PropTypes.bool,
    displayRequiredClassName: PropTypes.bool,
    floatingLabelClassName: PropTypes.string,
    floatingLabelShrink: PropTypes.bool,
    floatingLabelText: PropTypes.node,
    hasError: PropTypes.bool,
    htmlFor: PropTypes.any,
    inputWrapperProps: PropTypes.object,
    multiline: PropTypes.bool,
    onControlsClick: PropTypes.func,
    onInputWrapperClick: PropTypes.func,
    outline: PropTypes.bool,
    readOnly: PropTypes.bool,
    required: PropTypes.bool,
    sizeSmall: PropTypes.bool,
    tooltip: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element
    ]),
    type: PropTypes.string,
    value: PropTypes.any
};
export { Input };
export default withDefaultComponentProps(COMPONENT_NAME, {
    displayRequiredClassName: true,
    inputWrapperProps: {}
}, Input);
